import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

const initialState = {
  examFunctionFields: [],
  examFunctionFieldsJson: null, // Add this for JSON data
  isLoading: false,
  examFunctionFieldsJsonLoading: false, // Add separate loading state for JSON
  groupReportStatistics: null,
  groupReportStatisticsLoading: false,
  error: null,
};

export const fetchExamFunctionFields = createAsyncThunk(
  "groups/fetchExamFunctionFields",
  async ({ groups, formId, tests, testFields, workers, examIds, exportName, demographics, fieldConfigurations, filterGender, filterAgeRange, filterWorkerTags, logoPath, format, export_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    let url = `${apiBaseUrl}/exports/exam_function_fields`;

    const payload = {
      groups,
      formId,
      examIds,
      tests,
      testFields,
      workers,
      exportName,
      demographics,
      fieldConfigurations,
      filterGender,
      filterAgeRange,
      filterWorkerTags: filterWorkerTags ?? null,
      logoPath,
      format
    };

    if (export_id) {
      url += `?export_id=${export_id}`;
    }

    try {
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        responseType: 'blob', // For Excel/CSV download
      });

      return {
        blob: response.data,
        filename: getFilenameFromHeader(
          response.headers["content-disposition"],
          format || "xlsx"
        ),
        contentType: response.headers["content-type"],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// New thunk for fetching tests based on visit template ID
export const fetchTestsForVisitTemplate = createAsyncThunk(
  "groups/fetchTestsForVisitTemplate",
  async ({ visitTemplateId, selectedGroups }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/visit_templates/${visitTemplateId}/tests?groups=${JSON.stringify(selectedGroups)}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// New thunk for fetching group report statistics
export const generateGroupReport = createAsyncThunk(
  "groups/generateGroupReport",
  async ({ groups, formId, tests, testFields, workers, examIds, exportName, demographics, fieldConfigurations, filterGender, filterAgeRange, filterWorkerTags, logoPath, exportId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/group_reports`;

    const payload = {
      groups,
      formId,
      examIds,
      tests,
      testFields,
      workers,
      exportName,
      demographics,
      fieldConfigurations,
      filterGender,
      filterAgeRange,
      filterWorkerTags: filterWorkerTags ?? null,
      logoPath,
      exportId: exportId || null,
    };

    try {
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
        responseType: 'blob', // For Excel download
      });

      return {
        blob: response.data,
        filename: getFilenameFromHeader(
          response.headers["content-disposition"],
          "xlsx"
        ),
        contentType: response.headers["content-type"],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchGroupReportPreview = createAsyncThunk(
  "groups/fetchGroupReportPreview",
  async ({ groups, tests, testFields, fieldConfigurations, examIds, exportId, filterWorkerTags }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const baseUrl = `${apiBaseUrl}/group_reports/preview`;
    const payload = {
      group_ids: groups || [],
      test_ids: tests || [],
      test_field_ids: testFields || [],
      exam_ids: examIds || [],
      export_id: exportId || null,
      filter_worker_tags: filterWorkerTags || null,
      field_configurations: fieldConfigurations || {},
    };

    try {
      const response = await axios.post(baseUrl, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchGroupReportStatistics = createAsyncThunk(
  "groups/fetchGroupReportStatistics",
  async ({ testIds, examIds, testFieldIds, calculatedFieldIds, groupIds, exportId, accountId, filterWorkerTags }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const baseUrl = `${apiBaseUrl}/group_reports`;

    // Build query parameters for arrays
    const params = new URLSearchParams();
    if (accountId) {
      params.append('account_id', accountId);
    }
    if (testIds && testIds.length > 0) {
      testIds.forEach(id => params.append('test_ids[]', id));
    }
    if (examIds && examIds.length > 0) {
      examIds.forEach(id => params.append('exam_ids[]', id));
    }
    if (testFieldIds && testFieldIds.length > 0) {
      testFieldIds.forEach(id => params.append('test_field_ids[]', id));
    }
    if (calculatedFieldIds && calculatedFieldIds.length > 0) {
      calculatedFieldIds.forEach(id => params.append('calculated_field_ids[]', id));
    }
    if (groupIds && groupIds.length > 0) {
      groupIds.forEach(id => params.append('group_ids[]', id));
    }
    if (exportId) {
      params.append('export_id', exportId);
    }
    if (filterWorkerTags) {
      params.append('filter_worker_tags', filterWorkerTags);
    }

    const url = `${baseUrl}?${params.toString()}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const groupReportSlice = createSlice({
  name: "groupReport",
  initialState,
  reducers: {
    clearExamFunctionFieldsJson: (state) => {
      state.examFunctionFieldsJson = null;
    },
    setExamFunctionFieldsJson: (state, action) => {
      state.examFunctionFieldsJson = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // CSV Export
      .addCase(fetchExamFunctionFields.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchExamFunctionFields.fulfilled, (state, action) => {
        state.isLoading = false;
        state.examFunctionFields = action.payload;
      })
      .addCase(fetchExamFunctionFields.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error("Failed to fetch exam function fields");
      })

      // Fetch Tests for Visit Template
      .addCase(fetchTestsForVisitTemplate.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTestsForVisitTemplate.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle the tests data if needed in state
      })
      .addCase(fetchTestsForVisitTemplate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error("Failed to fetch tests for visit template");
      })

      // Group Report Statistics
      .addCase(fetchGroupReportStatistics.pending, (state) => {
        state.groupReportStatisticsLoading = true;
        state.error = null;
      })
      .addCase(fetchGroupReportStatistics.fulfilled, (state, action) => {
        state.groupReportStatisticsLoading = false;
        state.groupReportStatistics = action.payload;
      })
      .addCase(fetchGroupReportStatistics.rejected, (state, action) => {
        state.groupReportStatisticsLoading = false;
        state.error = action.payload;
        toast.error("Failed to fetch group report statistics");
      })
      .addCase(generateGroupReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(generateGroupReport.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(generateGroupReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        toast.error("Failed to download group report");
      });
  },
});

const getFilenameFromHeader = (contentDisposition, extension = 'csv') => {
  const fallback = `export_${Date.now()}.${extension}`;

  if (!contentDisposition) return fallback;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
  if (utf8Match) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDisposition.match(/filename="([^"]+)"/);
  if (asciiMatch) {
    return asciiMatch[1];
  }

  return fallback;
};

export const { clearExamFunctionFieldsJson, setExamFunctionFieldsJson } = groupReportSlice.actions;

export const selectExamFunctionFields = (state) => state.groupReport.examFunctionFields;
export const selectExamFunctionFieldsJson = (state) => state.groupReport.examFunctionFieldsJson;
export const selectExamFunctionFieldsJsonLoading = (state) => state.groupReport.examFunctionFieldsJsonLoading;
export const selectGroupReportStatistics = (state) =>
  state.groupReport?.groupReportStatistics ?? null;

export const selectGroupReportStatisticsLoading = (state) =>
  state.groupReport?.groupReportStatisticsLoading ?? false;

export const selectIsLoading = (state) => state.groupReport.isLoading;
export const selectError = (state) => state.groupReport.error;

export default groupReportSlice.reducer;